import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import { generateUrls } from '@hooks/usePaginationRule'
import usePaginationRules from '@hooks/usePaginationRules'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import PaginationRuleListItem from '../PaginationRuleListItem'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
      },
      key: 'brand_id',
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  const paginationRulesPayload = usePaginationRules({
    filters: pageFilters,
    performHttpRequests: true,
    requestOptions: {
      include_pagination_rule_brand: true,
    },
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredPaginationRules,
    filteredPaginationRulesCount,
    hasFilteredPaginationRules,
    loading,
  } = paginationRulesPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters, showCreateOrEditPaginationRuleModal, updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { paginationRulesIndexUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Pagination Rules | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => showCreateOrEditPaginationRuleModal()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey="paginationRules"
        tabBarItems={[
          {
            href: paginationRulesIndexUrl,
            icon: faCheckCircle,
            key: 'paginationRules',
            title: 'Pagination Rules',
          },
        ]}
        title="Pagination Rules"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredPaginationRules && <ListBlankState />}

            {!loading && hasFilteredPaginationRules && (
              <DashboardModule title="Pagination Rules">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    {filteredPaginationRules.map(paginationRule => (
                      <PaginationRuleListItem key={paginationRule.id} paginationRule={paginationRule} />
                    ))}
                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredPaginationRulesCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
